export type ColDef = {
  id?: string;
  accessorKey?: string;
  Header?: string; // some code uses Header
  header?: string; // others use header
  widthMode?: 'fixed' | 'auto';
  size?: number | string; // existing value may be string or number
  // ...any other column props
};

export function computeColumnWidthsGrow(
  baseColumns: any[],
  data: Record<string, any>[],
  containerWidth = 0,
  opts?: {
    font?: string; // e.g. "14px Inter, system-ui, sans-serif"
    padding?: number; // px total (left + right)
    sampleSize?: number; // how many longest cells to measure per column
    iconsWidth?: number; // combined icons width in px (default 37)
  },
): any[] {
  const { font = '14px sans-serif', padding = 0, sampleSize = 200, iconsWidth = 37 } = opts || {};
  // guard: if no columns return empty
  if (!baseColumns || !baseColumns.length) return [];

  // guard: if no data, compute based on header only
  const hasData = Array.isArray(data) && data.length > 0;

  // create a single canvas context for measurement
  const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  const ctx = canvas ? canvas.getContext('2d') : null;

  // fallback estimate if canvas not available
  const avgCharPx = 8; // fallback px per character

  if (ctx) ctx.font = font;

  const measure = (text: unknown) => {
    const s = text == null ? '' : String(text);
    if (ctx) {
      // Math.ceil to avoid fractional pixel sizes and ensure enough room
      return Math.ceil(ctx.measureText(s).width);
    }
    // fallback estimate
    return Math.ceil(s.length * avgCharPx);
  };

  // helper to safely parse fixed size
  const parseFixedSize = (val: any) => {
    if (val == null) return 0;
    if (typeof val === 'number') return Math.max(0, Math.round(val));
    const n = parseInt(String(val), 10);
    return Number.isFinite(n) ? Math.max(0, n) : 0;
  };

  // if there is data and it's large, we'll only measure top-N longest by char length
  // for each column to reduce expensive measureText calls
  const n = hasData ? data.length : 0;

  // Precompute per-column measurement candidates if data is large
  const computedColumns = baseColumns.map((col) => {
    // fixed width columns: keep size but ensure icons are added if not already included
    if (col.widthMode === 'fixed') {
      const fixed = parseFixedSize(col.size);
      return { ...col, size: fixed };
    }

    const key = col.accessorKey || ''; // accessor used to read cell value
    const headerText = col.Header ?? col.header ?? '';
    const headerW = measure(headerText);

    // if no data, width based on header only
    if (!hasData) {
      const size = Math.ceil(Math.max(headerW, 0) + padding + iconsWidth);
      return { ...col, size };
    }

    // small dataset: measure every cell
    if (n <= sampleSize) {
      let maxCellW = 0;
      for (let i = 0; i < n; i++) {
        const v = data[i][key];
        const w = measure(v ?? '');
        if (w > maxCellW) maxCellW = w;
      }
      const calcuWidth = maxCellW > headerW + padding + iconsWidth ? maxCellW : headerW + padding + iconsWidth;
      const computed = calcuWidth;
      return { ...col, size: computed };
    }

    // large dataset: pick top `sampleSize` longest strings (by char length) and measure only them
    // build an array of { idx, len } to avoid allocating many strings
    const lens: { idx: number; len: number }[] = new Array(n);
    for (let i = 0; i < n; i++) {
      const v = data[i][key];
      lens[i] = { idx: i, len: v == null ? 0 : String(v).length };
    }

    // partial sort to get top sampleSize by length. We'll use .sort() but truncate — OK for typical sizes
    lens.sort((a, b) => b.len - a.len);
    const limit = Math.min(sampleSize, n);
    let maxMeasured = 0;
    for (let i = 0; i < limit; i++) {
      const val = data[lens[i].idx][key];
      const w = measure(val ?? '');
      if (w > maxMeasured) maxMeasured = w;
    }

    const calcWidth =
      maxMeasured > headerW + padding + iconsWidth
        ? maxMeasured
        : Math.ceil(Math.max(headerW, maxMeasured) + padding + iconsWidth);

    const finalSize = calcWidth;
    return { ...col, size: finalSize };
  });

  const original = computedColumns.map((c: any) => parseFixedSize(c.size));
  const totalMeasured = original.reduce((a, b) => a + b, 0);

  // already fills container OR containerWidth not provided
  if (!containerWidth || totalMeasured >= containerWidth) {
    return computedColumns.map((c: any) => {
      const sz = parseFixedSize(c.size);
      return { ...c, size: sz, minSize: sz, maxSize: sz };
    });
  }

  // Compute extra space available
  const extra = containerWidth - totalMeasured;

  // Identify growable columns (those that are not `noGrow` and not widthMode:'fixed')
  const growableIndices: number[] = [];
  for (let i = 0; i < computedColumns.length; i++) {
    const c = computedColumns[i];
    const noGrow = !!c.noGrow; // per-column flag: set `noGrow: true` to prevent growth
    if (!noGrow) growableIndices.push(i);
  }

  // If nothing can grow, return measured widths unchanged
  if (growableIndices.length === 0) {
    return computedColumns.map((c: any) => {
      const sz = parseFixedSize(c.size);
      return { ...c, size: sz, minSize: sz, maxSize: sz };
    });
  }

  // Sum of base sizes for growable columns (use measured values)
  let growableBaseTotal = 0;
  for (const idx of growableIndices) {
    growableBaseTotal += original[idx] === 0 ? 1 : original[idx]; // treat zero as 1 to avoid 0 weights
  }

  // Distribute extra proportionally among growable columns
  const result = computedColumns.map((c: any, i: number) => {
    const base = original[i];
    const isGrowable = growableIndices.includes(i);
    if (!isGrowable) {
      const sz = parseFixedSize(base);
      return { ...c, size: sz, minSize: sz, maxSize: sz };
    }
    const weight = (original[i] === 0 ? 1 : original[i]) / growableBaseTotal;
    const add = Math.round(extra * weight);
    const size = Math.max(0, Math.round(base + add));
    return { ...c, size, minSize: size, maxSize: size };
  });

  // Fix rounding drift: ensure sum == containerWidth
  const newTotal = result.reduce((acc: number, c: any) => acc + parseFixedSize(c.size), 0);
  const diff = containerWidth - newTotal;
  if (diff !== 0) {
    // prefer to adjust first growable column; fallback to first column
    const adjustIdx = growableIndices.length > 0 ? growableIndices[0] : 0;
    const col = result[adjustIdx];
    const corrected = Math.max(0, parseFixedSize(col.size) + diff);
    result[adjustIdx] = {
      ...col,
      size: corrected,
      minSize: corrected,
      maxSize: corrected,
    };
  }
  return result;
}
